import { AlertTriangle } from "@untitledui/icons";
import { useId } from "react";
import {
	DialogTrigger as AriaDialogTrigger,
	Heading as AriaHeading,
} from "react-aria-components";
import {
	Dialog,
	Modal,
	ModalOverlay,
} from "@/components/application/modals/modal";
import { Button } from "@/components/base/buttons/button";
import { useAppTranslation } from "@/hooks/use-app-translation";
import type { AdminFormI18nPaths } from "../../admin-form-editor-i18n-paths";

export interface DeleteFormRuleConfirmProps {
	isOpen: boolean;
	ruleName: string;
	paths: AdminFormI18nPaths;
	onConfirm: () => void;
	onCancel: () => void;
}

/**
 * Modal-style confirmation dialog for removing a form rule from the in-memory
 * editor draft. Deletion only takes effect when the parent saves the form.
 *
 * Uses the shared React Aria {@link Modal} primitives so the overlay portals
 * to the document body and covers the full viewport, even when an ancestor
 * (e.g. the form editor shell) applies a `transform` that would otherwise
 * trap a hand-rolled `position: fixed` overlay inside its bounding box.
 */
export function DeleteFormRuleConfirm({
	isOpen,
	ruleName,
	paths,
	onConfirm,
	onCancel,
}: DeleteFormRuleConfirmProps) {
	const { t } = useAppTranslation();
	const titleId = useId();
	const bodyId = useId();

	return (
		<AriaDialogTrigger
			isOpen={isOpen}
			onOpenChange={(open) => {
				if (!open) onCancel();
			}}
		>
			<ModalOverlay isDismissable={false}>
				<Modal className="sm:max-w-md">
					<Dialog
						role="alertdialog"
						aria-labelledby={titleId}
						aria-describedby={bodyId}
					>
						<div className="w-full rounded-2xl bg-primary p-6 shadow-xl">
							<div className="flex items-start gap-3">
								<div
									className="flex size-10 shrink-0 items-center justify-center rounded-full bg-error-50 text-error"
									aria-hidden
								>
									<AlertTriangle className="size-5" />
								</div>
								<div className="min-w-0 flex-1">
									<AriaHeading
										slot="title"
										id={titleId}
										className="text-lg font-semibold text-primary"
									>
										{t(paths.formRules.delete.title)}
									</AriaHeading>
									<p id={bodyId} className="mt-1 text-sm text-tertiary">
										{t(paths.formRules.delete.body, { name: ruleName })}
									</p>
								</div>
							</div>
							<div className="mt-6 flex justify-end gap-3">
								<Button color="secondary" size="sm" onClick={onCancel}>
									{t(paths.formRules.delete.cancel)}
								</Button>
								<Button
									color="primary-destructive"
									size="sm"
									onClick={onConfirm}
								>
									{t(paths.formRules.delete.confirm)}
								</Button>
							</div>
						</div>
					</Dialog>
				</Modal>
			</ModalOverlay>
		</AriaDialogTrigger>
	);
}
